import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { shopOwnerAPI } from '../services/api';

const ShopOwnersPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [shopOwners, setShopOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [deletingOwner, setDeletingOwner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    shopOwnerType: 'shopOwner',
    status: 'active',
    emailVerified: false,
    mobileVerified: false,
  });
  const [dialogError, setDialogError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (shopId) {
      fetchShopOwners();
    }
  }, [shopId]);

  const fetchShopOwners = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await shopOwnerAPI.getShopOwners(shopId);
      setShopOwners(response.data || []);
    } catch (err) {
      console.error('Error fetching shop owners:', err);
      setError(err.response?.data?.message || 'Failed to fetch shop owners');
    } finally {
      setLoading(false);
    }
  };

  const defaultFormData = () => ({
    name: '',
    mobile: '',
    email: '',
    shopOwnerType: 'shopOwner',
    status: 'active',
    emailVerified: false,
    mobileVerified: false,
  });

  const handleOpenCreateDialog = () => {
    setEditingOwner(null);
    setFormData(defaultFormData());
    setDialogError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleOpenDialog = (owner) => {
    setEditingOwner(owner);
    setFormData({
      name: owner.name || '',
      mobile: owner.mobile || '',
      email: owner.email || '',
      shopOwnerType: owner.shopOwnerType || 'shopOwner',
      status: owner.status || 'active',
      emailVerified: owner.emailVerified || false,
      mobileVerified: owner.mobileVerified || false,
    });
    setDialogError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingOwner(null);
    setFormData(defaultFormData());
    setDialogError('');
    setSuccess('');
  };

  const handleOpenDeleteDialog = (owner) => {
    setDeletingOwner(owner);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingOwner(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.mobile.trim()) {
      setDialogError('Mobile number is required');
      return;
    }

    try {
      setSubmitting(true);
      setDialogError('');
      setSuccess('');

      const payload = {
        name: formData.name.trim(),
        mobile: formData.mobile.trim(),
        email: formData.email.trim() || undefined,
        shopOwnerType: formData.shopOwnerType,
        status: formData.status,
        emailVerified: formData.emailVerified,
        mobileVerified: formData.mobileVerified,
      };

      if (editingOwner) {
        await shopOwnerAPI.updateShopOwner(shopId, editingOwner._id, payload);
        setSuccess('Shop owner updated successfully');
      } else {
        await shopOwnerAPI.createShopOwner(shopId, payload);
        setSuccess('Shop owner created successfully');
      }
      fetchShopOwners();
      setTimeout(() => {
        handleCloseDialog();
      }, 1000);
    } catch (err) {
      console.error('Error updating shop owner:', err);
      setDialogError(
        err.response?.data?.message ||
          (editingOwner ? 'Failed to update shop owner' : 'Failed to create shop owner')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingOwner) return;

    try {
      setDeleting(true);
      await shopOwnerAPI.deleteShopOwner(shopId, deletingOwner._id);
      handleCloseDeleteDialog();
      fetchShopOwners();
    } catch (err) {
      console.error('Error deleting shop owner:', err);
      setError(err.response?.data?.message || 'Failed to delete shop owner');
      handleCloseDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'success';
    if (status === 'blocked') return 'error';
    return 'default';
  };

  const renderVerified = (verified) => (
    <Box display="flex" alignItems="center" gap={0.5}>
      {verified ? (
        <>
          <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="body2" color="success.main" fontSize="0.75rem">
            Yes
          </Typography>
        </>
      ) : (
        <>
          <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography variant="body2" color="error.main" fontSize="0.75rem">
            No
          </Typography>
        </>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && shopOwners.length === 0) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/shops')}
          sx={{ mb: 2 }}
        >
          Back to Shops
        </Button>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          gap={2}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/shops')}
            size={isMobile ? 'medium' : 'large'}
            fullWidth={isMobile}
          >
            {isMobile ? 'Back' : 'Back to Shops'}
          </Button>
          <Typography variant={isMobile ? 'h5' : 'h4'}>Shop Owners</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'large'}
        >
          Add Shop Owner
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {shopOwners.length === 0 ? (
        <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant={isMobile ? 'body1' : 'h6'} color="text.secondary" gutterBottom>
            No shop owners found
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This shop does not have any shop owners yet.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Add Shop Owner
          </Button>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {shopOwners.map((owner) => (
            <Card key={owner._id} sx={{ width: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" fontWeight="medium" sx={{ flex: 1 }}>
                    {owner.name || owner.mobile}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(owner)}
                      color="primary"
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(owner)}
                      color="error"
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>

                <Stack spacing={1.5}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">{owner.mobile}</Typography>
                  </Box>
                  {owner.email && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{owner.email}</Typography>
                    </Box>
                  )}
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <Chip label={owner.shopOwnerType} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
                    <Chip label={owner.status} size="small" color={getStatusColor(owner.status)} sx={{ textTransform: 'capitalize' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Mobile Verified
                    </Typography>
                    {renderVerified(owner.mobileVerified)}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Email Verified
                    </Typography>
                    {renderVerified(owner.emailVerified)}
                  </Box>
                  <Divider />
                  <Typography variant="caption" color="text.secondary">
                    Created: {formatDate(owner.createdAt)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '8px', sm: '16px' },
            },
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}>Name</TableCell>
                <TableCell sx={{ minWidth: 120 }}>Mobile</TableCell>
                <TableCell sx={{ minWidth: 150 }}>Email</TableCell>
                <TableCell sx={{ minWidth: 100 }}>Type</TableCell>
                <TableCell sx={{ minWidth: 90 }}>Status</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Mobile Verified</TableCell>
                <TableCell sx={{ minWidth: 110 }}>Email Verified</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Created At</TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shopOwners.map((owner) => (
                <TableRow key={owner._id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {owner.name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{owner.mobile}</TableCell>
                  <TableCell>{owner.email || '-'}</TableCell>
                  <TableCell>
                    <Chip label={owner.shopOwnerType} size="small" color="primary" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={owner.status} size="small" color={getStatusColor(owner.status)} sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>{renderVerified(owner.mobileVerified)}</TableCell>
                  <TableCell>{renderVerified(owner.emailVerified)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(owner.createdAt)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(owner)} color="primary" title="Edit">
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDeleteDialog(owner)} color="error" title="Delete">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingOwner ? 'Edit Shop Owner' : 'Add Shop Owner'}</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <TextField
            margin="dense"
            name="name"
            label="Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            sx={{ mt: 1 }}
          />
          <TextField
            margin="dense"
            name="mobile"
            label="Mobile *"
            fullWidth
            variant="outlined"
            value={formData.mobile}
            onChange={handleInputChange}
            required
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select name="shopOwnerType" value={formData.shopOwnerType} label="Type" onChange={handleInputChange}>
              <MenuItem value="shopOwner">Shop Owner</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="orderManger">Order Manager</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select name="status" value={formData.status} label="Status" onChange={handleInputChange}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={formData.mobileVerified} onChange={handleInputChange} name="mobileVerified" />}
            label="Mobile Verified"
            sx={{ mt: 1, display: 'block' }}
          />
          <FormControlLabel
            control={<Switch checked={formData.emailVerified} onChange={handleInputChange} name="emailVerified" />}
            label="Email Verified"
            sx={{ display: 'block' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : editingOwner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Shop Owner</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>{deletingOwner?.name || deletingOwner?.mobile}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopOwnersPage;
